import { Stack } from '../../../../../components/Stack';
import { useStepWizard } from '../../../../../components/StepWizard';
import { TextEditor } from '../../../../../components/TextEditor';
import { useClassHelper } from '../../../../../hooks/useClassHelper';
import { isTextChildrenEmpty } from '../../../helpers';
import { useDataDogActions } from '../../../hooks/useDataDogActions';
import { componentName } from '../constants';
import { useVoWifiRenderTracking } from '../hooks/useVoWifiRenderTracking';
import { PageProps, VoWifiSplash } from '../types';
import { Button } from './shared/Button';
import { HeaderImage } from './shared/HeaderImage';
import { Title } from './shared/Title';

interface Props extends PageProps, Partial<Omit<VoWifiSplash, 'type'>> {
  /**
   * Land two steps ahead instead of one. The template sets it when the step
   * after this one is the address form and the state already carries a
   * complete address; the form stays mounted so "Edit Address" can step back.
   */
  skipNextStep?: boolean;
}

const mockContent = [
  {
    type: 'paragraph',
    children: [
      {
        text: 'With Wi-Fi Calling, you can talk and text in places where mobile coverage is limited or unavailable.',
      },
    ],
  },
];

const defaultTexts = {
  title: 'Wi-Fi Calling',
  subtitle: mockContent,
  confirm: 'Continue',
} as const;

export const SplashScreen = ({
  themeValues,
  title,
  subtitle,
  confirm,
  isPortalPreview,
  skipNextStep = false,
}: Props) => {
  const { actions } = useDataDogActions({
    isPortalPreview,
    actionKey: 'vowifi',
  });

  const { containerRef, onImageError } = useVoWifiRenderTracking(
    isPortalPreview,
    'splash',
  );

  const c = useClassHelper(componentName);
  const { nextStep, goToStep, activeStep } = useStepWizard();

  const handleNextStep = () => {
    if (isPortalPreview) {
      return;
    }

    if (skipNextStep) {
      goToStep(activeStep + 2);
      return;
    }

    nextStep();
  };

  return (
    <div ref={containerRef} className={c('__splash-screen')}>
      <Stack gap={10} direction="column" justify="start">
        <HeaderImage
          src={themeValues.logoUrl}
          isPortalPreview={isPortalPreview}
          onError={() => onImageError(themeValues.logoUrl)}
        />
        <Title fontFamily={themeValues.fontTitle}>
          {title ? title : defaultTexts.title}
        </Title>

        <TextEditor
          viewOnly
          value={
            isTextChildrenEmpty(subtitle) ? defaultTexts.subtitle : subtitle
          }
          valueFontParagraph={themeValues.fontParagraph}
        />
        <Button
          type="button"
          onClick={handleNextStep}
          data-dd-action-name={
            actions?.vowifi_initial_screen_continue_button_clicked
          }
        >
          {confirm ? confirm : defaultTexts.confirm}
        </Button>
      </Stack>
    </div>
  );
};
